import React, { useState } from 'react'
import { Box, Container, Grid, Typography, TextField, Button, Stack } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import fastwalesfibreBg from '../assets/fastwalesfibre.webp'

/**
 * CoverageSection - Let users check if our broadband service is available in their area
 *
 * This component provides a postcode checker and shows a placeholder for the interactive map.
 * The map will eventually show live coverage areas across Wales.
 */
const CoverageSection: React.FC = () => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [postcode, setPostcode] = useState('')

  const handleCheckCoverage = () => {
    if (postcode.trim()) {
      navigate(`/check-services?postcode=${encodeURIComponent(postcode.trim())}`)
    }
  }
  return (
    <Box component="section" id="coverage-section" sx={{ 
      bgcolor: 'white', 
      backgroundImage: `linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.85) 100%), url('${fastwalesfibreBg}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      py: { xs: 8, md: 8 },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(25, 118, 210, 0.1) 50%, transparent 100%)',
      }
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left column - Text content */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Typography
                sx={{
                  color: '#8A8F98',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  fontWeight: 900,
                  fontSize: 18,
                }}
              >
                {t('coverage.title')}
              </Typography>
              
              <Typography
                component="h2"
                sx={{
                  color: '#0A1929',
                  fontWeight: 800,
                  fontSize: { xs: 32, md: 42 },
                  lineHeight: 1.2
                }}
              >
                {t('coverage.heading')}
              </Typography>

              <Typography
                sx={{
                  color: '#4A5568',
                  fontSize: { xs: 17, md: 19 },
                  lineHeight: 1.7,
                }}
              >
                {t('coverage.description')}
              </Typography>
            </Stack>
          </Grid>

          {/* Right column - Postcode input and button */}
          <Grid item xs={12} md={6}>
            <Stack spacing={2.5}>
              <TextField
                placeholder={t('coverage.postcodePlaceholder')}
                variant="outlined"
                fullWidth
                size="medium"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCheckCoverage()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                    color: '#1F2937',
                    borderRadius: 2,
                    minHeight: { xs: 56, md: 'auto' },
                    fontSize: { xs: '1rem', md: 'inherit' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease',
                    '& fieldset': { 
                      borderColor: '#E5E7EB',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': { 
                      borderColor: '#1976d2',
                      borderWidth: 2,
                    },
                    '&.Mui-focused fieldset': { 
                      borderColor: '#1976d2',
                      borderWidth: 2,
                      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: { xs: 1.75, md: 2 },
                    fontSize: { xs: '1rem', md: '1rem' },
                    fontWeight: 500,
                  },
                  '& .MuiInputBase-input::placeholder': { 
                    color: '#9CA3AF', 
                    opacity: 1,
                    fontWeight: 400,
                  },
                }}
              />
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleCheckCoverage}
                disabled={!postcode.trim()}
                sx={{
                  bgcolor: '#1976d2',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.75, md: 1.5 },
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.95rem', md: '0.9rem' },
                  minHeight: { xs: 44, md: 'auto' },
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    bgcolor: '#1565c0',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0) scale(0.98)',
                  },
                  '&:disabled': {
                    bgcolor: '#ccc',
                    cursor: 'not-allowed',
                  },
                }}
              >
                {t('coverage.checkButton')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default CoverageSection


